import { useEffect } from 'react';
import { useLocation } from 'react-router';

const SITE = 'Evidence Timeline';

/** O que a página diz de si a quem a indexa e a quem a partilha. */
export interface PageMetaProps {
  /** Sem o nome do sítio: é acrescentado aqui, uma vez, na mesma ordem. */
  readonly title?: string | undefined;
  readonly description?: string | undefined;
  /** Imagem do cartão social. Só entra se for uma URL absoluta e licenciada. */
  readonly image?: string | null | undefined;
  readonly type?: 'website' | 'article';
  /** JSON-LD já montado. Ver `PageMeta.tsx` para o que **não** usamos. */
  readonly jsonLd?: Record<string, unknown> | undefined;
}

/**
 * §16 §4 — o título, a descrição e o cartão social de cada página.
 *
 * Todas as páginas serviam `<title>Evidence Timeline</title>` e nenhuma
 * descrição: em resultados de pesquisa a app aparecia como dezenas de linhas
 * idênticas, e o motor escolhia sozinho um excerto qualquer da página. O
 * título e a descrição vêm agora do conteúdo que a página já carregou, e não
 * de uma tabela de strings à parte que envelhece sem ninguém reparar.
 *
 * **O que não fazemos, e porquê.** O schema.org tem `ClaimReview`, feito à
 * medida de um sítio que verifica afirmações — e exige um `reviewRating`, uma
 * nota numa escala. Este produto recusa-se a dar veredictos ([09]), e declarar
 * um só para ganhar um resultado enriquecido seria vender exactamente aquilo
 * que ele existe para não fazer. As afirmações são marcadas como `CreativeWork`,
 * que descreve o que a página é sem pontuar o que ela discute.
 *
 * **O limite disto.** Estas etiquetas são escritas por JavaScript depois de a
 * rota resolver. O Google executa JavaScript e vê-as; os leitores de cartões
 * sociais — WhatsApp, LinkedIn, Slack — na maioria não executam, e vêem os
 * valores por omissão do `index.html`. Fechar essa diferença exige pré-render
 * ou SSR, que é uma mudança de arquitectura e não uma etiqueta a mais.
 */
export function PageMeta({ title, description, image, type = 'website', jsonLd }: PageMetaProps) {
  const { pathname, search } = useLocation();

  useEffect(() => {
    const full = title === undefined || title.length === 0 ? SITE : `${title} · ${SITE}`;
    document.title = full;

    const url = `${window.location.origin}${pathname}${search}`;
    const set = (selector: string, attr: 'name' | 'property', key: string, value: string) => {
      let tag = document.head.querySelector<HTMLMetaElement>(selector);
      if (tag === null) {
        tag = document.createElement('meta');
        tag.setAttribute(attr, key);
        tag.dataset['etMeta'] = 'true';
        document.head.appendChild(tag);
      }
      tag.content = value;
    };

    if (description !== undefined && description.length > 0) {
      set('meta[name="description"]', 'name', 'description', description);
      set('meta[property="og:description"]', 'property', 'og:description', description);
      set('meta[name="twitter:description"]', 'name', 'twitter:description', description);
    }
    set('meta[property="og:title"]', 'property', 'og:title', full);
    set('meta[property="og:type"]', 'property', 'og:type', type);
    set('meta[property="og:url"]', 'property', 'og:url', url);
    set('meta[property="og:site_name"]', 'property', 'og:site_name', SITE);
    set('meta[name="twitter:title"]', 'name', 'twitter:title', full);
    /*
      `summary_large_image` só quando há mesmo imagem. Declarado sem ela, o
      cartão sai como um rectângulo vazio com o título por baixo — pior do
      que o cartão pequeno, que é o que um sítio sem imagem deve mostrar.
    */
    set('meta[name="twitter:card"]', 'name', 'twitter:card',
      image == null ? 'summary' : 'summary_large_image');
    if (image != null && image.length > 0) {
      set('meta[property="og:image"]', 'property', 'og:image', image);
      set('meta[name="twitter:image"]', 'name', 'twitter:image', image);
    } else {
      for (const stale of document.head.querySelectorAll(
        'meta[property="og:image"], meta[name="twitter:image"]',
      )) stale.remove();
    }

    for (const stale of document.head.querySelectorAll('script[data-et-jsonld]')) stale.remove();
    if (jsonLd !== undefined) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.dataset['etJsonld'] = 'true';
      script.textContent = JSON.stringify({ '@context': 'https://schema.org', ...jsonLd });
      document.head.appendChild(script);
    }
  }, [title, description, image, type, jsonLd, pathname, search]);

  return null;
}
